/** Cell coercion for table exports. Takes a cell as `extractTable` produced it
 *  (`components/Chat/Markdown/tableExtract.ts`) and types it for a spreadsheet.
 *
 *  A spreadsheet whose numbers arrive as strings cannot be summed, which is the
 *  reason Excel was asked for. But over-eager coercion destroys data: Excel
 *  eating the leading zero off `007`, or reading a part number as a date, is a
 *  reported class of finance bug. So the rule here is deliberately narrow —
 *  coerce only on a FULL-string match of a strict pattern, and leave everything
 *  else as text. Text is visible and fixable; a wrong number is neither.
 *
 *  See docs/superpowers/specs/2026-09-22-chat-table-exports-design.md,
 *  "Cell coercion (xlsx)".
 */

/** One extracted table cell: the text a reader saw, plus the URL if the cell
 *  was a link.
 *
 *  This is what `extractTable` produces and what the export writers consume, so
 *  `coerceCell` takes the whole cell rather than `(text, href?)`: passing the
 *  cell through in one piece is what makes dropping the hyperlink — the defect
 *  this shape was introduced to fix — impossible to do by accident. */
export interface TableCell {
  /** The cell's visible text, whitespace already collapsed. */
  text: string;
  /** `href` of the cell's first link, absent when it holds none. */
  href?: string;
}

export interface CoercedCell {
  /** The typed value. `Date` only for unambiguous ISO dates — see `parseIsoDate`. */
  v: string | number | Date;
  /** Excel number-format code, when the source string implied one. */
  fmt?: string;
  /** URL to attach as a real hyperlink, carried through from the cell. */
  link?: string;
}

/** Currency symbols we recognise as a leading marker. */
const CURRENCY_SYMBOLS = "$£€¥₹";

/** A markdown table uses these to mean "no value"; an empty cell sums and
 *  filters correctly where a literal dash pollutes a numeric column. */
const EMPTY_PLACEHOLDERS = new Set(["", "—", "–"]);

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Digits, western thousands grouping only, optional decimal.
 *
 *  Group 1 is the integer part (commas included), group 2 the decimal digits.
 *
 *  `1,23,456` (lakh grouping) is rejected: accepting every grouping scheme
 *  would mean accepting `1.234`, and a `.` separator is indistinguishable from
 *  a decimal point. See the European-notation note in `parseNumber`. */
const DIGITS = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/;

function build(v: CoercedCell["v"], fmt?: string, link?: string): CoercedCell {
  const cell: CoercedCell = { v };
  if (fmt !== undefined) cell.fmt = fmt;
  if (link !== undefined) cell.link = link;
  return cell;
}

/** ISO `YYYY-MM-DD` only. `03/04/2026` is March 4th in the US and 4th March in
 *  the UK; silently guessing wrong in a finance export is worse than leaving a
 *  string, so every other date shape stays text. */
function parseIsoDate(text: string): Date | null {
  const match = ISO_DATE.exec(text);
  if (!match) return null;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC rolls out-of-range parts over (month 13 becomes the next January),
  // so an impossible date only shows up on the round trip.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/** Strip each recognised affix exactly once, then require the remainder to
 *  match `DIGITS` in full. Anything left over means no coercion.
 *
 *  European notation (`1.234,56`) is deliberately NOT parsed. That exact shape
 *  cannot be US, but its column neighbours can be: `1.234` is 1234 in Berlin
 *  and 1.234 in Boston, and a chat table carries no locale. Coercing the
 *  unambiguous shape while leaving the ambiguous one as text yields a column of
 *  mixed types — which is precisely what stops Excel summing it. */
function parseNumber(text: string): CoercedCell | null {
  let body = text;
  let negative = false;
  let symbol = "";

  // Take a leading `-`, if any. Returns false — no coercion — on a second sign:
  // `(-5)` is -5 to one reader and +5 to another, and guessing is this module's
  // one forbidden move.
  const takeMinus = (): boolean => {
    if (!body.startsWith("-")) return true;
    if (negative) return false;
    negative = true;
    body = body.slice(1).trim();
    return true;
  };

  // Likewise a leading currency symbol, at most one.
  const takeSymbol = (): boolean => {
    if (body.length === 0 || !CURRENCY_SYMBOLS.includes(body[0])) return true;
    if (symbol) return false;
    symbol = body[0];
    body = body.slice(1).trim();
    return true;
  };

  // Sign and symbol are written in either order, and the accounting parenthesis
  // sits on either side of the symbol — `-$5`, `$-5`, `($500)` and `$(500)` are
  // one value spelled four ways, all of them unambiguous. Take each affix
  // wherever it turns up rather than fixing an order: accepting one spelling
  // and refusing its twin gives a column of mixed types, which is precisely
  // what stops Excel summing it.
  if (!takeMinus() || !takeSymbol()) return null;

  const parenthesised = /^\(([\s\S]*)\)$/.exec(body);
  if (parenthesised) {
    // Accounting convention: `(1,234)` is -1234.
    if (negative) return null; // `-(5)`, the same double sign as `(-5)`.
    negative = true;
    body = parenthesised[1].trim();
  }

  // Again, for the affixes written on the inside: `$-5`, `($500)`. Each helper
  // is a no-op when its affix is absent and a refusal when it is a repeat.
  if (!takeMinus() || !takeSymbol()) return null;

  let percent = false;
  if (body.endsWith("%")) {
    percent = true;
    body = body.slice(0, -1).trim();
  }

  if (symbol && percent) return null; // `$45%` is not a number, it is a typo.

  const match = DIGITS.exec(body);
  if (!match) return null;

  const grouped = match[1].includes(",");
  const integer = match[1].replace(/,/g, "");
  const fraction = match[2] ?? "";

  // A leading zero marks an identifier — a GL account, a zip, a part number.
  // Coercing `007` to 7 loses the zero for good; avoiding that is the whole
  // point of keeping this rule narrow.
  if (integer.length > 1 && integer.startsWith("0")) return null;

  // Trailing zeros in the fraction carry no value — `500.00` and `500` are the
  // same number — so they are not part of the round trip. Their COUNT still is:
  // it sets the display precision in `fmt` below.
  const significant = fraction.replace(/0+$/, "");
  const digits = significant ? `${integer}.${significant}` : integer;
  let value = Number(digits);

  // The magnitude guard. A double holds 15-17 significant digits, so a longer
  // run is silently rewritten by the cast: `123456789012345678` lands as
  // ...680 and `9999999999999999999` as 1e19. Long account numbers, invoice
  // references and IMEIs are ordinary ERP cell values, and a wrong number is
  // invisible in a way wrong text is not — the same reasoning that keeps `007`
  // text. So: a coerced number must round-trip to exactly the digits it came
  // from, or it stays text.
  //
  // Comparing the round trip rather than capping the digit count is deliberate
  // — it also catches long decimals (which lose precision at a different
  // length) and anything `Number` renders in exponent form, both without a
  // second rule to keep in step. It runs before the percent and sign
  // transforms because the invariant is about the digits as typed; `12.5%`
  // must round-trip against `12.5`, not against `0.125`.
  if (String(value) !== digits) return null;

  if (percent) value /= 100;
  if (negative) value = -value;

  const decimals = fraction ? `.${"0".repeat(fraction.length)}` : "";
  let fmt: string | undefined;
  if (percent) fmt = `0${decimals}%`;
  else if (symbol) fmt = `"${symbol}"#,##0${decimals}`;
  else if (grouped) fmt = `#,##0${decimals}`;

  return build(value, fmt);
}

/** Turn one extracted table cell into a typed spreadsheet cell. Pure.
 *
 *  `cell.text` is treated as literal text, never as markdown. remark/rehype
 *  resolves `**bold**`, `` `code` `` and `[label](url)` into elements before
 *  `extractTable` ever walks the tree, and extraction flattens those to their
 *  text — so syntax reaching here is data a user typed. Stripping it would
 *  rewrite `a * b * c` to `a  b  c` and `SKU_001_X` to `SKU001X` for the
 *  benefit of a caller that cannot exist. */
export function coerceCell(cell: TableCell): CoercedCell {
  const trimmed = cell.text.trim();
  const link = cell.href;

  if (EMPTY_PLACEHOLDERS.has(trimmed)) return build("", undefined, link);

  const date = parseIsoDate(trimmed);
  if (date) return build(date, "yyyy-mm-dd", link);

  const number = parseNumber(trimmed);
  if (number) return build(number.v, number.fmt, link);

  return build(trimmed, undefined, link);
}
